import { useCallback, useState } from "react";

import { useAppDispatch } from "../../../../redux/hooks";
import { setCertificateId, getCertificateDetails } from "../../../../actions/certificate_validator";

type TPngValidationResult =
    | {
          credentialId: string;
      }
    | {
          error: "certificate-id-missing";
      };

export const usePngValidation = (environment: TEnv, api_identifier: string, brandName: TAccountType) => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);

    /**
     * Extracts JSON metadata from PNG file chunks
     * Searches through PNG tEXt and iTXt chunks to find embedded JSON data
     * @param uint8Array - PNG file as Uint8Array
     * @returns Parsed JSON object or null if no valid JSON found
     */
    const extractJsonFromPng = useCallback((uint8Array: Uint8Array) => {
        // PNG files have chunks. Looking for text chunk with JSON
        let i = 8; // Skip PNG header

        while (i < uint8Array.length - 8) {
            // Read chunk length
            const length =
                (uint8Array[i] << 24) | (uint8Array[i + 1] << 16) | (uint8Array[i + 2] << 8) | uint8Array[i + 3];

            // Read chunk type
            const type = String.fromCharCode(
                uint8Array[i + 4],
                uint8Array[i + 5],
                uint8Array[i + 6],
                uint8Array[i + 7]
            );

            // Look for text chunks (tEXt, iTXt)
            if (type === "tEXt" || type === "iTXt") {
                const dataStart = i + 8;
                const dataEnd = dataStart + length;

                if (dataEnd <= uint8Array.length) {
                    let textData = "";

                    if (type === "tEXt") {
                        // tEXt chunk: keyword\0text
                        for (let j = dataStart; j < dataEnd; j++) {
                            textData += String.fromCharCode(uint8Array[j]);
                        }
                    } else if (type === "iTXt") {
                        // iTXt chunk: keyword\0compression\0language\0translated_keyword\0text
                        let nullCount = 0;
                        let textStart = dataStart;

                        for (let j = dataStart; j < dataEnd; j++) {
                            if (uint8Array[j] === 0) {
                                nullCount++;
                                if (nullCount === 4) {
                                    textStart = j + 1;
                                    break;
                                }
                            }
                        }

                        for (let j = textStart; j < dataEnd; j++) {
                            textData += String.fromCharCode(uint8Array[j]);
                        }
                    }

                    // Check if text contains JSON
                    if (textData.includes("{") && textData.includes("}")) {
                        try {
                            // Find JSON part
                            const jsonStart = textData.indexOf("{");
                            const jsonStr = textData.substring(jsonStart);
                            const jsonData = JSON.parse(jsonStr);

                            return jsonData;
                        } catch (e) {
                            console.log("JSON parsing error:", e);
                        }
                    }
                }
            }

            // Move to next chunk
            i += 8 + length + 4; // 8 bytes header + data + 4 bytes CRC
        }

        return null;
    }, []);

    const validatePng = useCallback(
        async (file: File): Promise<TPngValidationResult> => {
            setLoading(true);

            try {
                // Read file as ArrayBuffer
                const arrayBuffer = await file.arrayBuffer();
                const uint8Array = new Uint8Array(arrayBuffer);

                // Extract JSON data from PNG
                const jsonData = extractJsonFromPng(uint8Array);

                if (!jsonData) {
                    return { error: "certificate-id-missing" };
                }

                // Look for credentialId in the structure
                const credentialId = jsonData.extensions?.["extensions:extraDescription"]?.narrative;

                if (!credentialId || typeof credentialId !== "string") {
                    return { error: "certificate-id-missing" };
                }

                // Wait for getCertificateDetails to complete.
                // `as any` is used here because of the legacy Redux Thunk typing issues. Need to be refactored later.
                await dispatch(getCertificateDetails(credentialId, api_identifier, environment, brandName) as any);
                dispatch(setCertificateId(credentialId));

                return { credentialId };
            } catch (error) {
                return { error: "certificate-id-missing" };
            } finally {
                setLoading(false);
            }
        },
        [extractJsonFromPng, dispatch, api_identifier, environment, brandName]
    );

    return {
        validatePng,
        loading,
    };
};
